import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  Inject,
  PLATFORM_ID,
  OnDestroy
} from '@angular/core'
import { ReplaySubject } from 'rxjs'
import { filter, mapTo, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators'
import { isPlatformBrowser } from '@angular/common'

@Component({
  selector: 'flo-video-preview',
  templateUrl: './video-preview.component.html',
  styleUrls: ['./video-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VideoPreviewComponent implements OnDestroy {
  @Input() readonly src: string
  @Input() readonly posterImage: string
  @Output() readonly videoError = new EventEmitter<boolean>()
  public ngOnDestroy$ = new ReplaySubject<void>(1)
  public readonly handleHlsMessages$ = new ReplaySubject<any>(1)
  private readonly contentVideoElement$ = new ReplaySubject<HTMLVideoElement>(1)
  public readonly isBrowser$ = new ReplaySubject<boolean>(1)

  /**
   * Not sure what this type is for the output of the MSE directive. May need to create a
   * new interface.
   */
  @ViewChild('videoElement')
  set contentVideoElement(contentVideoElement: ElementRef<HTMLVideoElement>) {
    if (contentVideoElement && contentVideoElement.nativeElement) {
      this.contentVideoElement$.next(contentVideoElement.nativeElement)
    }
  }
  // tslint:disable-next-line:ban-types
  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser$.next(isPlatformBrowser(platformId))
  }

  /**
   * Making sure to not call play() on server since that does not exist. Then if any kind of error occurs
   * from the play event we trying playing one more time and if it errors once more we kill the video upstream.
   */
  public readonly play = this.isBrowser$
    .pipe(
      switchMap(() => this.contentVideoElement$),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(videoElement => {
      videoElement.play().catch(_e => {
        videoElement.muted = true
        videoElement.play().catch(() => {
          this.videoError.emit(true)
        })
      })
    })

  /**
   * if any network error occurs we default to showing the image thumbnail. We let things like buffer errors still occur
   */
  private videoError$ = this.handleHlsMessages$.pipe(
    filter(e => e.key === 'hlsError'),
    filter(e => e.message.type === 'networkError'),
    mapTo(true),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  private emitVideoError$ = this.videoError$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(err => {
    this.videoError.emit(err)
  })

  ngOnDestroy() {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }
}
